import { CardImage, Integration, PaymentOption } from '../../model'
import type { Money } from '@afterpay/types'
import {
  useApplePayEntryData,
  useCheckoutToken,
  useConsumerSelector,
  useCheckoutState,
  useCohort,
  usePaymentOptions,
  useSelectedPaymentOption,
  useOfferDetails,
  authLogout,
  closeCheckout,
  persistor,
  useAppDispatch,
  useAuthentication,
  useRedirectUrl,
  useCreateCheckoutMutation,
  CREATE_CHECKOUT_FIXED_KEY,
} from '../../state'
import { buildSaveOfferRequest, buildIssueCardRequest } from './utils'
import type { AppleGatewaySaveOfferRequest, AppleGatewayIssueCardRequest, UserCohort } from '../../state/appleGateway/apis'
import { Cohort, OfferDetail } from '@afterpaytouch/portal-api/types'
import { valueIsNotNullOrUndefined } from '@afterpay/utils'
import { app } from '../../env'
import { TrackingEvent } from '../../model/monitoring'
import { useMerchantCallback } from '../../utils/merchant'
import { getCurrentUserContextFromClient, useLDClient } from '@afterpaytouch/feature'
import { capture } from '@afterpaytouch/integrations/monitoring'
import { useTransactionMessage } from '../../utils/transactionMessage'
import { useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { stopDatadogSession } from '@afterpaytouch/integrations/datadog'
import { useRef } from 'react'

/**
 * OfferDetail enriched with interestAmount from PaymentOption
 */
export interface EnrichedOfferDetail extends OfferDetail {
  interestAmount?: Money
}

export { useFinaliseApplePaylaterCheckout } from './hooks/useFinaliseApplePaylaterCheckout'

export const useAddedCardType = (): Integration | undefined => {
  const applePayEntryData = useApplePayEntryData()
  const addedCardConditions = [{ condition: applePayEntryData?.isValid, value: Integration.ApplePay }]

  const addedCardType = addedCardConditions.find(({ condition }) => condition)?.value
  return addedCardType
}

export const useCardImage = (): string => {
  const addedCardType = useAddedCardType()
  if (typeof addedCardType === 'undefined') {
    return ''
  }

  return CardImage[addedCardType]
}

// Map Cohort to UserCohort for API
const getUserCohort = (cohort: Cohort | undefined): UserCohort => {
  return cohort === Cohort.CashOnly ? 'CASHAPP' : 'AFTERPAY'
}

// Map PaymentOption to EnrichedOfferDetail for API
const mapPaymentOptionToOffer = (paymentOption: PaymentOption): EnrichedOfferDetail => ({
  monthlyAmount: paymentOption.installmentAmount,
  amountDueToday: paymentOption.amountDueToday,
  totalAmount: paymentOption.totalAmount,
  numberOfInstallments: paymentOption.installmentsCount,
  apr: paymentOption.apr?.toString(),
  paymentType: paymentOption.paymentType,
  interestAmount: paymentOption.interestAmount ?? undefined,
})

// Map OfferDetail from checkout/start to EnrichedOfferDetail format
const mapOfferDetailToEnriched = (offer: OfferDetail): EnrichedOfferDetail => ({
  monthlyAmount: offer.monthlyAmount,
  amountDueToday: offer.amountDueToday,
  totalAmount: offer.totalAmount,
  numberOfInstallments: offer.numberOfInstallments,
  apr: offer.apr,
  paymentType: offer.paymentType,
})

export const useSaveOfferRequest = (): AppleGatewaySaveOfferRequest | null => {
  const token = useCheckoutToken()
  const consumer = useConsumerSelector()
  const { started } = useCheckoutState()
  const cohort = useCohort()
  const paymentOptions = usePaymentOptions()
  const selectedPaymentOption = useSelectedPaymentOption()
  const offerDetails = useOfferDetails()
  const [, { data: createCheckoutData }] = useCreateCheckoutMutation({ fixedCacheKey: CREATE_CHECKOUT_FIXED_KEY })

  const userCohort = getUserCohort(cohort)
  // change later, when payment option is available, this should from payment options
  const paymentSchedules = started?.paymentSchedules
  const expires = createCheckoutData?.expires

  const hasPaymentOptions = paymentOptions.length > 0

  const allOffers = hasPaymentOptions ? paymentOptions.map(mapPaymentOptionToOffer) : offerDetails.map(mapOfferDetailToEnriched)

  let selectedOffer: EnrichedOfferDetail | undefined

  if (hasPaymentOptions && valueIsNotNullOrUndefined(selectedPaymentOption)) {
    selectedOffer = mapPaymentOptionToOffer(selectedPaymentOption)
  } else if (!hasPaymentOptions && allOffers.length > 0) {
    selectedOffer = allOffers[0]
  }

  return buildSaveOfferRequest(token, consumer, allOffers, selectedOffer, paymentSchedules, userCohort, expires)
}

export const useIssueCardRequest = (): AppleGatewayIssueCardRequest | null => {
  const token = useCheckoutToken()
  const consumer = useConsumerSelector()

  return buildIssueCardRequest(token, consumer)
}

/**
 * Apple Pay Later specific close checkout hook
 *
 * This hook is specifically designed for Apple Pay Later flow and uses
 * route detection to determine redirect behavior:
 * - If redirect URL contains APPLE_GATEWAY_REDIRECT_PATH: uses RTK Query to call proxy endpoint
 *   (needed for Apple Gateway staging URLs that require Basic Auth)
 * - Otherwise: performs direct redirect with query args appended
 *
 * Key differences from useCloseCheckout:
 * - Uses RTK Query proxy for Apple Gateway redirect routes (to handle Basic Auth server-side)
 * - Uses direct redirect for other routes (merchant callbacks, etc.)
 * - Only used in Apple Pay Later confirmation flow
 * - Keeps other flows (regular checkout, etc.) unaffected
 */
export const useCloseApplePaylater: () => (fetchedRedirectUrl: string | undefined, queryArgs: string) => Promise<void> = () => {
  const { initial, checkout, confirmed, referrer } = useCheckoutState()
  const { endTransaction } = useMerchantCallback()
  const { authenticated, persistent } = useAuthentication()
  const dispatch = useAppDispatch()
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const redirectUrl = useRedirectUrl()
  const LDClient = useLDClient()
  const closeCount = useRef(1)
  const endTransactionMessage = useTransactionMessage()

  const reportError = (message: string, error): void => {
    logEvent(TrackingEvent.CHECKOUT_FATAL_ERROR, {
      error: message,
      merchantName: checkout?.merchant.name,
      merchantId: checkout?.merchant.id,
    })

    const context = {
      'Merchant Data': { ...checkout?.merchant },
      'Order confirmed?': { confirmed: typeof confirmed !== 'undefined', ...confirmed },
      'Error details': { ...error },
    }

    capture.error(error, context)
  }

  const clearSession = async (): Promise<void> => {
    dispatch(closeCheckout())
    if (persistor === null) {
      return
    }
    await persistor.flush()
  }

  const getCallbackUrl = (): string => {
    if (redirectUrl.length === 0) {
      reportError('No baseCallbackUrl to return to', undefined)
      return referrer ?? app.BROCHURE_BASE_URL
    }
    const callbackUrl = redirectUrl + (initial?.relativeCallbackUrl ?? '')
    return callbackUrl
  }

  const trackCloseCount = (): void => {
    logEvent(TrackingEvent.CLOSE_CALLED, {
      closeCount: closeCount.current,
    })
    closeCount.current += 1
  }

  return async (fetchedRedirectUrl: string | undefined, queryArgs: string) => {
    const logout = async (): Promise<void> => {
      if (authenticated && !persistent) {
        await dispatch(authLogout())
      }
    }

    const redirect = async (): Promise<void> => {
      trackCloseCount()

      if (fetchedRedirectUrl !== undefined && fetchedRedirectUrl.length > 0) {
        window.location.href = fetchedRedirectUrl
      } else {
        const callbackUrl = getCallbackUrl()
        const fullUrl = callbackUrl + (queryArgs.length > 0 ? (callbackUrl.includes('?') ? '&' : '?') + queryArgs : '')
        window.location.href = fullUrl
      }
    }

    const closePopup = async (): Promise<void> => {
      try {
        await endTransaction(endTransactionMessage)
      } catch (e) {
        reportError('No confirmation of end transaction', e)
      }
      trackCloseCount()
      await clearSession()
      if (!initial.isIframe) {
        // If we are using zoid then afterpay.js is responsible for closing the iframe
        window.close()
      }
    }

    const key = getCurrentUserContextFromClient(LDClient)?.key ?? null
    if (typeof LDClient !== 'undefined' && key !== null) {
      await LDClient.close()
    }

    await logout()

    stopDatadogSession()

    if (initial.isWindowed || initial.isIframe) {
      await closePopup()
    } else {
      await redirect()
    }
  }
}
